import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCalcLeite } from './CalcleiteContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './Capital.css';
import Config from '../../config/config.json';
import tags from '../../config/tags.json';

// --- FUNÇÕES AUXILIARES DE FORMATAÇÃO ---
const formatCurrencyForDisplay = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const number = Number(String(value).replace(/\D/g, '')) / 100;
  if (isNaN(number)) return '';
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
};

const formatPercentageForDisplay = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const cleanedValue = String(value).replace(',', '.');
  const number = parseFloat(cleanedValue);
  if (isNaN(number)) return '';
  return number.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const Capital = () => {
  const { dados, setDados } = useCalcLeite();
  const navigate = useNavigate();

  const [mostrarResultados, setMostrarResultados] = useState(false);
  const [mostrarAjuda, setMostrarAjuda] = useState(false);

  useEffect(() => {
    if (!dados.capital) {
      setDados(prev => ({
        ...prev,
        capital: {
          benfeitoriasMaquinas: '', areaPasto: '', areaForragensInverno: '',
          rebanho: '', remuneracaoCapital: '', taxaDepreciacao: ''
        }
      }));
    }
    if (dados.resultadosCapital) {
      setMostrarResultados(true);
    }
  }, [dados.capital, dados.resultadosCapital, setDados]);
  
  const handleCurrencyChange = (e) => {
    const { name, value } = e.target;
    const rawValue = value.replace(/\D/g, '');
    setDados(prev => ({
      ...prev,
      capital: { ...prev.capital, [name]: rawValue }
    }));
  };

  const handlePercentageChange = (e) => {
    const { name, value } = e.target;
    const rawValue = value.replace(/[^0-9,.]/g, '').replace(',', '.');
    setDados(prev => ({
      ...prev,
      capital: { ...prev.capital, [name]: rawValue }
    }));
  };

  const handleCalcular = () => {
    const leiteProduzido = (Number(dados.producao?.leiteProduzido) || 0) / 100;
    if (leiteProduzido === 0) {
      alert("'Litros de Leite Produzido' na tela de Produção precisa ser maior que zero.");
      return;
    }
    
    const c = dados.capital;
    const benfeitoriasMaquinas = (Number(c.benfeitoriasMaquinas) || 0) / 100;
    const areaPasto = (Number(c.areaPasto) || 0) / 100;
    const areaForragensInverno = (Number(c.areaForragensInverno) || 0) / 100;
    const rebanho = (Number(c.rebanho) || 0) / 100;
    
    const remuneracaoCapitalPercent = (parseFloat(c.remuneracaoCapital) || 0) / 100;
    const taxaDepreciacaoPercent = (parseFloat(c.taxaDepreciacao) || 0) / 100;

    const capitalTotalInvestido = benfeitoriasMaquinas + areaPasto + areaForragensInverno + rebanho;
    const rentabilidadeCapital = capitalTotalInvestido * remuneracaoCapitalPercent;
    const rentabilidadeCapitalLitro = rentabilidadeCapital / leiteProduzido;
    const depreciacao = benfeitoriasMaquinas * taxaDepreciacaoPercent;
    const depreciacaoLitro = depreciacao / leiteProduzido;

    const resultados = {
      capitalTotalInvestido: capitalTotalInvestido.toFixed(2), rentabilidadeCapital: rentabilidadeCapital.toFixed(2),
      rentabilidadeCapitalLitro: rentabilidadeCapitalLitro.toFixed(2), depreciacao: depreciacao.toFixed(2),
      depreciacaoLitro: depreciacaoLitro.toFixed(2),
    };

    setDados(prev => ({ ...prev, resultadosCapital: resultados }));
    setMostrarResultados(true);
  };

  const formatCurrencyResult = (value) => {
    const num = parseFloat(value || 0);
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };
  
  // --- LÓGICA DO PDF CORRIGIDA E ESTILIZADA ---
  const handleDownloadPDF = () => {
    if (!dados.capital || !dados.resultadosCapital) {
      alert("Por favor, calcule os resultados antes de gerar o PDF.");
      return;
    }
    const doc = new jsPDF();
    const c = dados.capital;
    const r = dados.resultadosCapital;
    
    // Cabeçalho com fundo azul
    doc.setFillColor(30, 144, 255); // #1e90ff
    doc.rect(0, 0, 210, 40, 'F'); // Cabeçalho com 40mm de altura
    doc.setTextColor(255, 255, 255); // Texto branco
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text("Relatório de Capital Investido", 105, 20, { align: 'center' });

    // Data centralizada abaixo do título
    doc.setTextColor(200, 200, 200); // Cinza claro
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    const dataFormatada = new Date(dados.producao?.dataSelecionada).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
    doc.text(`Data: ${dataFormatada}`, 105, 30, { align: 'center' });

    // Adicionar imagens no cabeçalho
    const custosAgroImg = Config.colors.base64.custosagro;
    const appImg = Config.colors.base64[tags.tags.app];
    if (custosAgroImg) {
      doc.addImage(custosAgroImg, 'PNG', 10, 7.5, 25, 25); // Logo CustosAgro mais à esquerda
    }
    if (appImg) {
      doc.addImage(appImg, 'PNG', 175, 7.5, 25, 25); // Logo do app mais à direita
    }

    // Tabela de Dados Inseridos
    doc.setTextColor(80, 80, 80);
    doc.setFontSize(11);
    const dadosInseridosBody = [
      ['Benfeitorias, Máquinas e Equipamentos', `${formatCurrencyResult(Number(c.benfeitoriasMaquinas) / 100)}`],
      ['Área de Pasto', `${formatCurrencyResult(Number(c.areaPasto) / 100)}`],
      ['Área de Forragens de Inverno', `${formatCurrencyResult(Number(c.areaForragensInverno) / 100)}`],
      ['Rebanho', `${formatCurrencyResult(Number(c.rebanho) / 100)}`],
      ['Remuneração do Capital', `${c.remuneracaoCapital}%`],
      ['Taxa de Depreciação', `${c.taxaDepreciacao}%`],
    ];

    autoTable(doc, {
      startY: 50,
      head: [['Descrição', 'Valor Inserido']],
      body: dadosInseridosBody,
      theme: 'striped',
      headStyles: { fillColor: [44, 62, 80] },
    });

    // Tabela de Resultados Calculados
    const resultadosBody = [
      ['Capital Total Investido', `${formatCurrencyResult(r.capitalTotalInvestido)}`],
      ['Rentabilidade do Capital', `${formatCurrencyResult(r.rentabilidadeCapital)}`],
      ['Rentabilidade por Litro', `${formatCurrencyResult(r.rentabilidadeCapitalLitro)}`],
      ['Depreciação', `${formatCurrencyResult(r.depreciacao)}`],
      ['Depreciação por Litro', `${formatCurrencyResult(r.depreciacaoLitro)}`],
    ];

    autoTable(doc, {
      head: [['Indicador', 'Resultado Calculado']],
      body: resultadosBody,
      theme: 'grid',
      headStyles: { fillColor: [30, 144, 255] },
    });

    // Adicionar numeração de páginas
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.setTextColor(150, 150, 150);
      doc.text(`Relatório gerado por CustosAgro - Página ${i} de ${pageCount}`, 105, 287, { align: 'center' });
    }
    
    doc.save("relatorio_capital.pdf");
  };

  const handleLimparDados = () => {
    if(window.confirm("Tem certeza que deseja limpar os dados desta tela?")) {
      setDados(prev => {
        const { capital, resultadosCapital, ...resto } = prev;
        localStorage.setItem('calcLeiteData', JSON.stringify(resto));
        return resto;
      });
      setMostrarResultados(false);
    }
  };

  return (
    <div className="capital-container">
      <div className="capital-card">
        <h1>Capital Investido</h1>
        <p className="capital-subtitle">Preencha os campos para calcular os indicadores.</p>

        <div className="capital-form-results-grid">
          <div className="capital-form">
            <div className="capital-input-group">
              <label>Benfeitorias, Máquinas e Equipamentos (R$)</label>
              <input type="text" name="benfeitoriasMaquinas" placeholder="Ex: 500.000,00" 
                     value={formatCurrencyForDisplay(dados.capital?.benfeitoriasMaquinas)} onChange={handleCurrencyChange} />
            </div>
            <div className="capital-input-group">
              <label>Área de Pasto (R$)</label>
              <input type="text" name="areaPasto" placeholder="Ex: 300.000,00" 
                     value={formatCurrencyForDisplay(dados.capital?.areaPasto)} onChange={handleCurrencyChange} />
            </div>
            <div className="capital-input-group">
              <label>Área de Forragens de Inverno (R$)</label>
              <input type="text" name="areaForragensInverno" placeholder="Ex: 50.000,00" 
                     value={formatCurrencyForDisplay(dados.capital?.areaForragensInverno)} onChange={handleCurrencyChange} />
            </div>
            <div className="capital-input-group">
              <label>Rebanho (R$)</label>
              <input type="text" name="rebanho" placeholder="Ex: 800.000,00" 
                     value={formatCurrencyForDisplay(dados.capital?.rebanho)} onChange={handleCurrencyChange} />
            </div>
            <div className="capital-input-group">
              <label>Remuneração do Capital Investido (%)</label>
              <input type="text" name="remuneracaoCapital" placeholder="Ex: 1,00" 
                     value={dados.capital?.remuneracaoCapital} onChange={handlePercentageChange} />
            </div>
            <div className="capital-input-group">
              <label>Taxa de Depreciação (%)</label>
              <input type="text" name="taxaDepreciacao" placeholder="Ex: 1,50" 
                     value={dados.capital?.taxaDepreciacao} onChange={handlePercentageChange} />
            </div>
            <button className="capital-button" onClick={handleCalcular}>Calcular</button>
          </div>

          {mostrarResultados && (
            <div className="capital-results">
              <h2>Resultados</h2>
              <ul>
                <li>Capital Total Investido: <strong>{formatCurrencyResult(dados.resultadosCapital?.capitalTotalInvestido)}</strong></li>
                <li>Rentabilidade do Capital: <strong>{formatCurrencyResult(dados.resultadosCapital?.rentabilidadeCapital)}</strong></li>
                <li>Rentabilidade por Litro: <strong>{formatCurrencyResult(dados.resultadosCapital?.rentabilidadeCapitalLitro)}</strong></li>
                <li>Depreciação: <strong>{formatCurrencyResult(dados.resultadosCapital?.depreciacao)}</strong></li>
                <li>Depreciação por Litro: <strong>{formatCurrencyResult(dados.resultadosCapital?.depreciacaoLitro)}</strong></li>
              </ul>
              <button className="capital-button pdf" onClick={handleDownloadPDF}>Baixar Relatório (PDF)</button>
            </div>
          )}
        </div>
        
        <button className="capital-help-button" onClick={() => setMostrarAjuda(true)}>Precisa de ajuda?</button>

        <div className="capital-navigation">
          <button className="capital-button back" onClick={() => navigate('/calcleite/app/producao')}>Voltar para Produção</button>
          <button className="capital-button clean" onClick={handleLimparDados}>Limpar Dados</button>
          <button className="capital-button navigate" onClick={() => navigate("/calcleite/app/custosCafe")}>Avançar para Custos</button>
        </div>
      </div>

      {mostrarAjuda && (
        <div className="ajuda-modal-backdrop" onClick={() => setMostrarAjuda(false)}>
          <div className="ajuda-modal-content" onClick={e => e.stopPropagation()}>
            <h2>Ajuda - Capital Investido</h2>
            <p><strong>Custo Capital Investido:</strong> É o que o produtor deixa de ganhar se vendesse todo seu patrimônio e aplicasse no mercado financeiro, em vez de investir no negócio do leite.</p>
            <p><strong>Dica:</strong> O custo do capital investido geralmente varia de 1% a 1,5% ao mês. A depreciação mensal também costuma variar na mesma faixa.</p>
            <button onClick={() => setMostrarAjuda(false)}>Fechar</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Capital;